// src/components/access/Guard.tsx
// Generic access gate for UI and interactions.
// - Hide or render a fallback when access is denied
// - Optionally "disable" interactive children (e.g., buttons, links) with a tooltip
//
// Works with the Policy produced by compilePolicy() from AccessPolicy.ts

import * as React from "react";
import type { Policy } from "@/lib/auth/AccessPolicy";
import { useAccessPolicy } from "@/hooks/access/useAccess";

// shadcn/ui tooltips are optional. If you don't use shadcn, we'll fall back to a native title.
let ShadTooltip: any = null;
let ShadTooltipTrigger: any = null;
let ShadTooltipContent: any = null;
try {
  // These imports are optional; if missing, the try/catch leaves them null
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const m = require("@/components/ui/tooltip");
  ShadTooltip = m.Tooltip;
  ShadTooltipTrigger = m.TooltipTrigger;
  ShadTooltipContent = m.TooltipContent;
} catch {}

export type GuardMode = "hide" | "render" | "disable";

export type GuardProps = {
  /**
   * Provide either `when` or `check`.
   * `when` is a raw boolean; `check` derives a boolean from Policy.
   */
  when?: boolean;
  check?: (policy: Policy) => boolean;

  /**
   * hide    - returns null when not allowed
   * render  - renders `fallback` when not allowed (default)
   * disable - renders children but disables interactions and shows tooltip
   */
  mode?: GuardMode;

  /** What to show when not allowed (mode=render). Default: null */
  fallback?: React.ReactNode;

  /** Tooltip or title when access is denied (mode=disable). */
  reason?: string | ((policy: Policy) => string);

  /**
   * Optional className(s) applied to the wrapper span when mode=disable
   * (we use a span to keep DOM tree shallow and preserve layout)
   */
  className?: string;

  /** Content to guard */
  children: React.ReactNode;
};

export function Guard(props: GuardProps) {
  const { mode = "render", fallback = null, className } = props;
  const policy = useAccessPolicy();
  const allowed = resolveAllowed(policy, props);

  if (allowed) return <>{props.children}</>;

  if (mode === "hide") return null;

  if (mode === "render") return <>{fallback}</>;

  // mode === "disable" -> render disabled UI + tooltip/title hint
  const reason = typeof props.reason === "function" ? props.reason(policy) : props.reason;

  // If children is a single element, try to clone and disable it. Otherwise, wrap.
  const child = getSingleElementOrNull(props.children);
  const disabledChild = child
    ? cloneDisabled(child)
    : (
        <span className="pointer-events-none opacity-60 select-none">
          {props.children}
        </span>
      );

  const content = (
    <span className={className} aria-disabled title={!ShadTooltip && reason ? reason : undefined}>
      {disabledChild}
    </span>
  );

  if (!reason || !ShadTooltip) return content;

  return (
    <ShadTooltip>
      <ShadTooltipTrigger asChild>
        {content}
      </ShadTooltipTrigger>
      <ShadTooltipContent>{reason}</ShadTooltipContent>
    </ShadTooltip>
  );
}

function resolveAllowed(policy: Policy, { when, check }: Pick<GuardProps, "when" | "check">) {
  if (typeof when === "boolean") return when;
  if (typeof check === "function") return !!check(policy);
  // Default to false if no condition was specified to avoid accidental leaks
  return false;
}

function getSingleElementOrNull(children: React.ReactNode): React.ReactElement | null {
  if (React.isValidElement(children)) return children as React.ReactElement;
  if (Array.isArray(children) && children.length === 1 && React.isValidElement(children[0])) {
    return children[0] as React.ReactElement;
  }
  return null;
}

// Try to disable common interactive elements by props. Works for shadcn Button, native button, links, etc.
function cloneDisabled(el: React.ReactElement) {
  // Build only the overrides; cloneElement merges with original props internally
  const disabledProps: any = {
    onClick: undefined,
    onPointerDown: undefined,
    onKeyDown: undefined,
    tabIndex: -1,
    "aria-disabled": true,
    className: mergeClass(
      (el.props as any)?.className,
      "pointer-events-none opacity-60 select-none"
    ),
  };

  // Common cases
  if (typeof (el.props as any).disabled !== "undefined") {
    disabledProps.disabled = true;
  }
  if (el.type === "a" && (el.props as any).href) {
    disabledProps.href = undefined; // strip link target
  }

  return React.cloneElement(el, disabledProps);
}

function mergeClass(a?: string, b?: string) {
  if (!a) return b || "";
  if (!b) return a || "";
  return `${a} ${b}`;
}

// -----------------------------------------------------------------------------
// src/components/access/ActionButton.tsx
// Drop-in button that bakes in guards + tooltip for denied actions.

import { Button } from "@/components/ui/button";
import type { ButtonHTMLAttributes } from "react";

export type ActionButtonProps = {
  /** Provide either `when` or `check` to determine if the action is allowed */
  when?: boolean;
  check?: (policy: Policy) => boolean;
  /** Reason shown in tooltip/title when disabled due to access. */
  reason?: string | ((policy: Policy) => string);
  /** Force hide instead of showing disabled state */
  hideWhenDenied?: boolean;
  /**
   * If you want a different component than shadcn Button, pass it via `as`.
   * Example: as={(p) => <SomeButton {...p} />}
   */
  as?: (p: ButtonHTMLAttributes<HTMLButtonElement>) => React.ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function ActionButton({ when, check, reason, hideWhenDenied, as, ...btn }: ActionButtonProps) {
  return (
    <Guard
      when={when}
      check={check}
      mode={hideWhenDenied ? "hide" : "disable"}
      reason={reason}
    >
      {as ? (
        as(btn)
      ) : (
        <Button {...btn} />
      )}
    </Guard>
  );
}

// -----------------------------------------------------------------------------
// Usage examples:
//
// 1) Hide a details button if user cannot read records:
//
// <Guard check={(p) => p.records.read} mode="hide">
//   <Button onClick={() => openDetails(id)}>Details</Button>
// </Guard>
//
// 2) Render a disabled button with a tooltip when the user cannot create records:
//
// <ActionButton
//   check={(p) => p.records.create}
//   reason={(p) => "Недостаточно прав для создания записи"}
//   onClick={() => openCreateModal()}
// >
//   Создать
// </ActionButton>
//
// 3) Gate a link but keep it visible and non-interactive when phone access is denied:
//
// <Guard
//   check={(p) => p.timetable.phonesVisible}
//   mode="disable"
//   reason="Телефон скрыт настройками доступа"
// >
//   <a href="tel:+79998887766" className="text-muted-foreground">+7••• •• ••••</a>
// </Guard>
//
// 4) Force-hide Staff dropdown when a staff is locked:
//
// <Guard check={(p) => !p.timetable.forcedStaffId} mode="hide">
//   <StaffDropdown />
// </Guard>
//
// 5) Combine with explicit boolean `when` if you computed access earlier:
//
// const canOpen = policy.records.read && policy.timetable.phonesVisible;
// <Guard when={canOpen} mode="render" fallback={<span className="opacity-50">N/A</span>}>
//   <SensitiveWidget />
// </Guard>
